import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './index.css';
import viewMoreArrow from '@/assets/imgs/recruit/view-more-arrow.svg';
import { ContactSection } from '@/pages/Home/components/HomeBusiness';
import { RecruitHero } from '../components/RecruitHero';
import {
  recruitmentPage,
  type RecruitmentRecord,
} from '@/service/api/recruitment';

const PAGE_SIZE = 10;
const targetTypeTranslationKeys: Record<string, string> = {
  full_time: 'recruit.list.targetType.fullTime',
  intern: 'recruit.list.targetType.intern',
};

function getDutyPreview(record: RecruitmentRecord) {
  const source = record.dutySummary?.trim() || record.duty?.trim() || record.requirement?.trim() || '';
  if (!source) return '';

  const withLineBreaks = source.replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n');
  if (typeof window === 'undefined') return withLineBreaks.replace(/<[^>]*>/g, '');

  const documentNode = new DOMParser().parseFromString(withLineBreaks, 'text/html');
  return (documentNode.body.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
}

type RecruitmentCardProps = {
  record: RecruitmentRecord;
};

function RecruitmentCard({ record }: RecruitmentCardProps) {
  const { t } = useTranslation();
  const preview = getDutyPreview(record);
  const targetType = record.targetType?.trim() || '';
  const targetTypeLabel = targetTypeTranslationKeys[targetType]
    ? t(targetTypeTranslationKeys[targetType])
    : targetType;

  return (
    <article className="recruit-list-card">
      <div className="recruit-list-card__content">
        <div className="recruit-list-card__heading">
          <h2>{record.title || t('recruit.list.untitled')}</h2>
          {targetType && (
            <span className="recruit-list-card__type">{targetTypeLabel}</span>
          )}
        </div>
        {preview && <p className="recruit-list-card__preview">{preview}</p>}
      </div>

      <Link
        to={`/recruit-detail?id=${record.id ?? ''}`}
        className="recruit-list-card__more"
        aria-label={`View More ${record.title || ''}`}
      >
        <span className="recruit-list-card__more-content">
          <span>View More</span>
          <span className="recruit-list-card__arrow" aria-hidden="true">
            <img src={viewMoreArrow} alt="" />
          </span>
        </span>
      </Link>
    </article>
  );
}

function RecruitmentSkeleton() {
  return (
    <div className="recruit-list-card recruit-list-card--skeleton" aria-hidden="true">
      <div className="recruit-list-card__skeleton-content">
        <div className="recruit-list-card__skeleton-title" />
        <div className="recruit-list-card__skeleton-line" />
        <div className="recruit-list-card__skeleton-line" />
        <div className="recruit-list-card__skeleton-line recruit-list-card__skeleton-line--short" />
      </div>
      <div className="recruit-list-card__skeleton-button" />
    </div>
  );
}

const RecruitList = () => {
  const { t, i18n } = useTranslation();
  const [records, setRecords] = useState<RecruitmentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let ignore = false;

    setLoading(true);
    setError(false);

    recruitmentPage({ current: 1, size: PAGE_SIZE, status: 1 })
      .then(result => {
        if (ignore) return;
        if (result.code !== 0) throw new Error(result.msg || 'Unable to load recruitment');
        setRecords(result.data?.records ?? []);
      })
      .catch(() => {
        if (ignore) return;
        setRecords([]);
        setError(true);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [i18n.resolvedLanguage]);

  return (
    <div className="recruit-list-page">
      <RecruitHero />

      <section className="recruit-list-section" aria-label={t('recruit.list.title')}>
        {loading ? (
          <div className="recruit-list-items">
            {Array.from({ length: 4 }, (_, index) => (
              <div key={index} className="recruit-list-item">
                <RecruitmentSkeleton />
              </div>
            ))}
          </div>
        ) : error || records.length === 0 ? (
          <div className="recruit-list-empty">
            {t(error ? 'recruit.list.error' : 'recruit.list.empty')}
          </div>
        ) : (
          <div className="recruit-list-items">
            {records.map((record, index) => (
              <div
                key={record.id ?? `${record.title}-${index}`}
                className="recruit-list-item"
              >
                <RecruitmentCard record={record} />
              </div>
            ))}
          </div>
        )}
      </section>

      <ContactSection overlapPrevious={false} />
    </div>
  );
};

export default RecruitList;
